import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Edit3, History } from 'lucide-react';
import {
  DashboardDetailsPageShell,
  DashboardDetailsTab,
  DashboardDetailsTabList,
  DashboardDetailsHeader,
  dashboardPageTitleClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, DropdownMenuItem } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { ChangelogDialog } from '../../../../changelog/components/ChangelogDialog';
import { useDemandPartnerQuery } from '../../hooks';

export function DemandPartnerDetailsPage(props: { demandPartnerId: string; children: ReactNode }) {
  const query = useDemandPartnerQuery(props.demandPartnerId);
  const { can } = useShigoPermissions();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const canEdit = can(ShigoPermission.DemandPartnersEdit);
  const canReadChangelog = can(ShigoPermission.DemandPartnersChangelogRead);
  const partner = query.data;
  return (
    <>
      <DashboardDetailsPageShell
        activeTab="overview"
        loading={query.isLoading}
        loadingLabel="Loading Demand Partner details"
        showHeaderAction={canEdit || canReadChangelog}
        tabCount={1}
        header={
          <DashboardDetailsHeader
            back={
              <ReturnBackLink
                fallback={{ to: '/demand/partners' }}
                label="Back to Demand Partners"
              />
            }
            title={
              <h1 className={dashboardPageTitleClassName('min-w-0 truncate')}>
                {partner?.displayName ?? partner?.name ?? 'Demand Partner'}
              </h1>
            }
            moreActionsLabel={dashboardT('demandPartners.moreActions')}
            moreActions={
              canReadChangelog && partner ? (
                <DropdownMenuItem
                  onSelect={() => {
                    setChangelogOpen(true);
                  }}
                >
                  <History className="size-4" aria-hidden="true" />
                  {dashboardT('demandPartners.changelog')}
                </DropdownMenuItem>
              ) : undefined
            }
            primaryAction={
              canEdit && partner ? (
                <Button asChild variant="ghost" size="sm">
                  <Link
                    to="/demand/partners/edit/$demandPartnerId"
                    params={{ demandPartnerId: partner.id }}
                  >
                    <Edit3 className="size-4" aria-hidden="true" />
                    Edit
                  </Link>
                </Button>
              ) : undefined
            }
          />
        }
        tabs={
          <DashboardDetailsTabList aria-label="Demand Partner details tabs">
            <DashboardDetailsTab value="overview" asChild>
              <Link
                to="/demand/partners/$demandPartnerId/details/overview"
                params={{ demandPartnerId: props.demandPartnerId }}
              >
                Overview
              </Link>
            </DashboardDetailsTab>
          </DashboardDetailsTabList>
        }
      >
        {query.isError ? (
          <div className="border-border bg-card space-y-4 rounded-md border p-6">
            <p>Demand Partner details could not be loaded.</p>
            <Button type="button" variant="outline" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          props.children
        )}
      </DashboardDetailsPageShell>
      {canReadChangelog && partner && changelogOpen ? (
        <ChangelogDialog
          title={dashboardT('demandPartners.changelogTitle', {
            name: partner.displayName ?? partner.name ?? '',
          })}
          description={dashboardT('demandPartners.changelogDescription')}
          scope={{ demandPartnerIds: [partner.id] }}
          onClose={() => {
            setChangelogOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
